import { Box, Tooltip } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { notificationStore } from '../store/Notification';

const STATUS_SUBMITTED = 'submitted';

const AWS_STATUS_STYLES: { [key: string]: { color: string; bg: string } } = {
  SUCCEEDED: { color: '#1b5e20', bg: '#c8e6c9' },
  FAILED: { color: '#b71c1c', bg: '#ffcdd2' },
  RUNNING: { color: '#0d47a1', bg: '#bbdefb' },
  STARTING: { color: '#0d47a1', bg: '#bbdefb' },
  RUNNABLE: { color: '#333333', bg: '#e0e0e0' },
  PENDING: { color: '#333333', bg: '#e0e0e0' },
  SUBMITTED: { color: '#333333', bg: '#e0e0e0' },
};

const isSubmitted = (status: string) =>
  (status || '').trim().toLowerCase() === STATUS_SUBMITTED;

const copyToClipboard = async (text: string, message: string) => {
  try {
    await navigator.clipboard.writeText(text);
    notificationStore.showMessage(message, 'info');
  } catch (err) {
    notificationStore.showMessage('Error during copy to clipboard', 'error');
  }
};

export const SubmissionStatusCell = ({
  status,
  logStream,
  skipReason,
}: {
  status: string;
  logStream?: string | null;
  skipReason?: string | null;
}) => {
  const ok = isSubmitted(status);
  const color = ok ? '#1b5e20' : '#b71c1c';
  const backgroundColor = ok ? '#c8e6c9' : '#ffcdd2';
  const reason = ok ? null : skipReason;

  const badge = (
    <Box
      component='span'
      onClick={() =>
        logStream &&
        copyToClipboard(logStream, 'Log stream id copied to clipboard.')
      }
      sx={{
        borderRadius: 1,
        px: 1,
        py: 0.25,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 102,
        boxSizing: 'border-box',
        gap: 0.5,
        fontSize: '0.875rem',
        cursor: logStream ? 'pointer' : 'default',
        color,
        backgroundColor,
      }}
    >
      {ok ? 'Submitted' : 'Failed'}
      {ok ? <Check fontSize='small' /> : <Close fontSize='small' />}
    </Box>
  );

  if (!logStream && !reason) {
    return badge;
  }

  const title = (
    <>
      {reason && <Box>Reason: {reason}</Box>}
      {logStream && <Box>Click to copy submission log stream: {logStream}</Box>}
    </>
  );

  return (
    <Tooltip title={title} arrow>
      {badge}
    </Tooltip>
  );
};

export const JobStatusCell = ({
  status,
  jobId,
  jobType,
}: {
  status?: string | null;
  jobId?: string | null;
  jobType: 'segmentation' | 'classification';
}) => {
  if (!status) {
    return <>-</>;
  }
  const key = status.toUpperCase();
  const style = AWS_STATUS_STYLES[key] || AWS_STATUS_STYLES.SUBMITTED;

  const copyJobId = () =>
    jobId &&
    copyToClipboard(
      jobId,
      `${jobType[0].toUpperCase()}${jobType.slice(1)} job ID copied.`
    );

  const badge = (
    <Box
      component='span'
      onClick={copyJobId}
      sx={{
        borderRadius: 1,
        px: 1,
        py: 0.25,
        display: 'inline-block',
        width: 100,
        boxSizing: 'border-box',
        textAlign: 'center',
        fontSize: '0.875rem',
        cursor: jobId ? 'pointer' : 'default',
        color: style.color,
        backgroundColor: style.bg,
      }}
    >
      {key}
    </Box>
  );

  return jobId ? (
    <Tooltip title={`Click to copy ${jobType} job id: ${jobId}`} arrow>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
};
